import React, { MouseEvent, useMemo, useRef, useState } from 'react';
import { IconArrowsChevronBottom, Text, useClassnames } from '@wb-tech/ui-react';
import { NavLink, useLocation } from 'react-router-dom';
import { Transition } from 'react-transition-group';

import { forceChangeSliderPosition } from '#helper/slider';

import type { INavItem } from '#component/header/types';

import style from './index.module.pcss';

interface IProps {
    item: INavItem,
    isMobile?: boolean,

    onClick?: () => void
}

export const HeaderNavItem = (props: IProps) => {
    const cn = useClassnames(style);
    const $options = useRef<HTMLDivElement>(null);
    const location = useLocation();

    const isDefaultOpen = useMemo(() => {
        const isActiveBase = location.pathname === props.item.to;
        const isActiveStartsWith = location.pathname.startsWith(props.item.to);
        const isActiveCharAt = location.pathname.charAt(props.item.to.length) === '/';

        return isActiveBase || (isActiveStartsWith && isActiveCharAt);
    }, [props.item, location]);

    const [isOpen, setIsOpen] = useState<boolean>(isDefaultOpen);

    const onClickShowOptions = (event: MouseEvent) => {
        event.preventDefault();

        setIsOpen((prevState) => !prevState);
    };

    const onClickMobileOption = () => {
        props?.onClick?.();

        forceChangeSliderPosition();
    };

    if(props.isMobile) {
        return (
            <div>
                <NavLink
                    to={props.item.to}
                    reloadDocument={props.item.reloadDocument}
                    className={({ isActive }) => {
                        return cn('nav-item', {
                            'nav-item_active': isActive
                        });
                    }}
                >
                    <Text presetColor="inherit">{props.item.text}</Text>
                    {props.item.options?.length && (
                        <button
                            className={cn('nav-item__icon-wrapper')}
                            onClick={onClickShowOptions}
                            type="button"
                        >
                            <IconArrowsChevronBottom
                                svg={{
                                    className: cn('nav-item__icon', {
                                        'nav-item__icon_opened': isOpen
                                    })
                                }}
                            />
                        </button>
                    )}
                </NavLink>
                {props.item.options?.length && (
                    <Transition
                        nodeRef={$options}
                        in={isOpen}
                        timeout={300}
                        unmountOnExit={true}
                        mountOnEnter={true}
                    >
                        {(state) => (
                            <div
                                className={cn('nav-item__options', {
                                    [`nav-item__options_${state}`]: state
                                })}
                                ref={$options}
                            >
                                {props.item.options?.map((option) => (
                                    <NavLink
                                        key={`${option.to}-${option.text}`}
                                        to={option.to}
                                        className={({ isActive }) => {
                                            return cn('nav-item__option', {
                                                'nav-item__option_active': isActive
                                            });
                                        }}
                                        onClick={onClickMobileOption}
                                    >
                                        {option.text}
                                        {option.additionalContent}
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </Transition>
                )}
            </div>
        );
    }

    return (
        <NavLink
            to={props.item.to}
            reloadDocument={props.item.reloadDocument}
            className={({ isActive }) => {
                return cn('nav-item', {
                    'nav-item_active': isActive
                });
            }}
        >
            <Text presetColor="inherit">{props.item.text}</Text>
        </NavLink>
    );
};
